import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Beef, Milk, Baby, Heart, Plus, TrendingUp, Loader2, Trash2, Check, X, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import LivestockManager from './LivestockManager';
import ProductionCalendar from '../ui/ProductionCalendar';
import { validateDate, validateQuantity } from '../../utils/validation';
import { todayLocal, daysAgoLocal, formatDateShort } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safely coerce a FastAPI/axios error payload to a display string. FastAPI
// returns `detail` as an ARRAY of Pydantic error objects for 422 validation
// failures — passing that array into <Toast> (or any JSX child) throws
// "Objects are not valid as a React child" and white-screens the dev overlay.
const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(', ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || fallback;
};

/**
 * LivestockPage — top-level 4-tab wrapper for the Livestock area.
 * Matches the Poultry tab's visual template.
 *
 *   Manage — species tabs (goat/sheep/cattle), herd cards, add/edit
 *   Milk   — quick milk entry + calendar + recent logs
 *   Births — quick birth entry (dam/sire dropdowns w/ inline creation
 *            + Unknown/pastured sire) + optional individual tracking
 *            + recent births + season rollup
 *   Health — quick health entry + recent records
 *
 * Weight logging stays inside individual animal profiles (per spec —
 * operators don't weigh regularly enough to justify a top-level tab).
 */
export default function LivestockPage() {
  const [activeTab, setActiveTab] = useState('manage');

  return (
    <div className="space-y-6 animate-fade-in w-full" data-testid="livestock-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
          <Beef className="w-7 h-7 text-primary" />
          Livestock
        </h1>
        <p className="text-muted-foreground mt-1">Goats, sheep, and cattle</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="manage" className="flex items-center gap-1.5" data-testid="livestock-manage-tab">
            <Beef className="w-4 h-4" />
            <span className="hidden sm:inline">Manage</span>
          </TabsTrigger>
          <TabsTrigger value="milk" className="flex items-center gap-1.5" data-testid="livestock-milk-tab">
            <Milk className="w-4 h-4" />
            <span className="hidden sm:inline">Milk</span>
          </TabsTrigger>
          <TabsTrigger value="births" className="flex items-center gap-1.5" data-testid="livestock-births-tab">
            <Baby className="w-4 h-4" />
            <span className="hidden sm:inline">Births</span>
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-1.5" data-testid="livestock-health-tab">
            <Heart className="w-4 h-4" />
            <span className="hidden sm:inline">Health</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manage" className="mt-6" data-testid="livestock-manage-content">
          <LivestockManager />
        </TabsContent>

        <TabsContent value="milk" className="mt-6 space-y-4" data-testid="livestock-milk-content">
          <MilkTab />
        </TabsContent>

        <TabsContent value="births" className="mt-6 space-y-4" data-testid="livestock-births-content">
          <BirthsTab />
        </TabsContent>

        <TabsContent value="health" className="mt-6 space-y-4" data-testid="livestock-health-content">
          <HealthTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// MILK TAB
// ============================================================================
function MilkTab() {
  const { getAuthHeader } = useAuth();
  const [foundationAnimals, setFoundationAnimals] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    foundation_animal_id: '',
    date: todayLocal(),
    quantity: '',
    unit: 'gallons',
    notes: '',
  });

  const load = async () => {
    try {
      // 90-day window matches LivestockManager. Backend requires start_date/end_date.
      const startDate = daysAgoLocal(90);
      const endDate = todayLocal();
      // Use the aggregated /logs/all endpoint so herd-level entries
      // (animal_type: null) also surface here — the per-species queries
      // filter by animal_type == goat|sheep|cattle and would skip them.
      const [fRes, calRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/animals/foundation`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/livestock/milk-production/calendar/all?start_date=${startDate}&end_date=${endDate}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/livestock/milk-production/logs/all?days=90`, { headers: getAuthHeader() }).catch(() => ({ data: { logs: [] } })),
      ]);
      // Only livestock (goat/sheep/cattle), females — potential dams that can be milked
      const animals = (fRes.data?.animals || fRes.data || []).filter(
        (a) => ['goat', 'sheep', 'cattle'].includes(a.animal_type) && a.sex === 'female'
      );
      setFoundationAnimals(animals);
      // Backend returns { daily, total, days_with_data, ... } directly — no wrapper key.
      setCalendarData(calRes.data || {});
      const logs = allRes.data?.logs || [];
      setRecentLogs(logs.slice(0, 20));
    } catch {
      /* soft-fail — empty state renders */
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (allowHistorical = false) => {
    const q = validateQuantity(form.quantity, { field: 'Quantity', max: 50 });
    if (!q.ok) { toast.error(q.message); return; }
    const dv = validateDate(form.date, { field: 'Date' });
    if (!dv.ok) {
      if (dv.reason === 'too_old') {
        // Client-side "too old" is only a *soft* block — if the user has
        // explicitly confirmed (allowHistorical=true) we let the request
        // through and rely on the backend allow_historical flag.
        if (!allowHistorical) {
          setPendingHistorical('milk');
          return;
        }
      } else {
        toast.error(dv.message);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/livestock/milk-production`,
        {
          foundation_animal_id: form.foundation_animal_id || null,
          date: form.date,
          quantity: parseFloat(form.quantity),
          unit: form.unit,
          notes: form.notes,
          allow_historical: allowHistorical,
        },
        { headers: getAuthHeader() }
      );
      // Optimistic prepend so the row shows immediately even if `load()`
      // hits a read-your-writes race with the backend. `load()` below then
      // reconciles with the authoritative list.
      const newLog = res.data?.log;
      if (newLog) setRecentLogs((prev) => [newLog, ...prev].slice(0, 20));
      toast.success('Milk logged');
      setForm({ ...form, quantity: '', notes: '' });
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to log milk'));
    } finally {
      setSaving(false);
    }
  };

  // Confirm-historical modal state — surfaced when validateDate returns
  // reason === 'too_old' so users can still log old entries deliberately.
  const [pendingHistorical, setPendingHistorical] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Delete + 5-second Undo. The record is optimistically removed from the list
  // and re-POSTed on Undo. We use `allow_historical: true` on the restore so
  // very old records survive re-insertion (they already passed validation).
  const handleDelete = async (record) => {
    setConfirmDeleteId(null);
    // Optimistic UI: drop from list immediately
    setRecentLogs((logs) => logs.filter((r) => r.id !== record.id));
    try {
      await axios.delete(`${API_URL}/api/livestock/milk-production/${record.id}`, { headers: getAuthHeader() });
      let undone = false;
      toast.success('Milk log deleted', {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            undone = true;
            try {
              await axios.post(`${API_URL}/api/livestock/milk-production`, {
                foundation_animal_id: record.foundation_animal_id || null,
                animal_type: record.animal_type || null,
                date: record.date,
                quantity: record.quantity,
                unit: record.unit || 'gallons',
                notes: record.notes || null,
                allow_historical: true,
              }, { headers: getAuthHeader() });
              toast.success('Restored');
              await load();
            } catch {
              toast.error('Restore failed');
              await load();
            }
          },
        },
      });
      // Refresh calendar totals after the toast window closes.
      setTimeout(() => { if (!undone) load(); }, 5100);
    } catch {
      toast.error('Delete failed');
      await load();
    }
  };

  return (
    <>
      <Card className="card-farm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4" /> Quick Milk Entry
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Animal (optional)</label>
            <Select value={form.foundation_animal_id || 'none'} onValueChange={(v) => setForm({ ...form, foundation_animal_id: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="milk-animal-select"><SelectValue placeholder="Herd-level (unattributed)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Herd-level (unattributed)</SelectItem>
                {foundationAnimals.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} · {a.animal_type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Quantity</label>
              <Input type="number" step="0.1" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="milk-quantity-input" placeholder="e.g., 1.5" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Unit</label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gallons">gallons</SelectItem>
                  <SelectItem value="liters">liters</SelectItem>
                  <SelectItem value="lbs">lbs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Date</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional" />
          </div>
          <Button onClick={() => submit()} disabled={saving} className="w-full" data-testid="milk-log-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Milk className="w-4 h-4 mr-2" />} Log Milk
          </Button>
        </CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Production Calendar</CardTitle></CardHeader>
        <CardContent><ProductionCalendar data={calendarData} /></CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent Logs</CardTitle></CardHeader>
        <CardContent data-testid="milk-recent-list">
          {recentLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No milk logged yet.</p>
          ) : (
            <ul className="divide-y">
              {recentLogs.map((r, i) => (
                <li key={r.id || i} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <span className="w-24 shrink-0">{formatDateShort(r.date)}</span>
                  <span className="text-muted-foreground truncate flex-1">{r.animal_type || ''}</span>
                  <span className="font-mono font-semibold">{r.quantity} {r.unit || 'gal'}</span>
                  {confirmDeleteId === r.id ? (
                    <span className="flex items-center gap-1 ml-1">
                      <span className="text-xs text-muted-foreground">Delete?</span>
                      <Button size="icon" variant="destructive" className="h-7 w-7"
                        onClick={() => handleDelete(r)}
                        data-testid={`milk-delete-confirm-${r.id}`}
                        aria-label="Confirm delete"
                      ><Check className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7"
                        onClick={() => setConfirmDeleteId(null)}
                        data-testid={`milk-delete-cancel-${r.id}`}
                        aria-label="Cancel delete"
                      ><X className="w-3.5 h-3.5" /></Button>
                    </span>
                  ) : (
                    r.id && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmDeleteId(r.id)}
                        data-testid={`milk-delete-${r.id}`}
                        aria-label="Delete milk log"
                      ><Trash2 className="w-3.5 h-3.5" /></Button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Historical-entry confirmation (>5 years old) */}
      <Dialog open={pendingHistorical === 'milk'} onOpenChange={(v) => !v && setPendingHistorical(null)}>
        <DialogContent data-testid="milk-historical-confirm">
          <DialogHeader>
            <DialogTitle>Log historical entry?</DialogTitle>
            <DialogDescription>
              The date you entered ({form.date}) is more than 5 years ago. If this is intentional
              (backfilling old records), we can save it — otherwise please pick a more recent date.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setPendingHistorical(null)}>Cancel</Button>
            <Button className="flex-1" onClick={() => { setPendingHistorical(null); submit(true); }} data-testid="milk-historical-confirm-btn">
              Save as historical
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// BIRTHS TAB
// ============================================================================
function BirthsTab() {
  const { getAuthHeader } = useAuth();
  const [dams, setDams] = useState([]);
  const [sires, setSires] = useState([]);
  const [recent, setRecent] = useState([]);
  const [season, setSeason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddDam, setShowAddDam] = useState(false);
  const [showAddSire, setShowAddSire] = useState(false);
  const [lastBirth, setLastBirth] = useState(null); // for post-birth "track individually" prompt

  const [form, setForm] = useState({
    dam_id: '',
    sire_id: '', // 'unknown' or actual id
    date: todayLocal(),
    number_born: 1,
    number_live: 1,
    notes: '',
  });

  const load = async () => {
    try {
      const [fRes, bRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/animals/foundation`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/birth-events?limit=20`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/birth-events/stats/season`, { headers: getAuthHeader() }),
      ]);
      const livestock = (fRes.data?.animals || fRes.data || []).filter((a) => ['goat', 'sheep', 'cattle'].includes(a.animal_type));
      setDams(livestock.filter((a) => a.sex === 'female'));
      setSires(livestock.filter((a) => a.sex === 'male'));
      setRecent(bRes.data?.events || bRes.data || []);
      setSeason(sRes.data || null);
    } catch { /* soft-fail */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async (allowHistorical = false) => {
    if (!form.dam_id) { toast.error('Select a dam'); return; }
    const born = Number(form.number_born) || 0;
    const live = Number(form.number_live) || 0;
    if (born < 1) { toast.error('Number born must be at least 1'); return; }
    if (live > born) { toast.error('Number live cannot exceed number born'); return; }
    const dv = validateDate(form.date, { field: 'Birth date' });
    if (!dv.ok) {
      if (dv.reason === 'too_old') {
        if (!allowHistorical) {
          setPendingHistorical(true);
          return;
        }
      } else {
        toast.error(dv.message);
        return;
      }
    }

    setSaving(true);
    try {
      // Build babies[] per backend BabyEntryCreate contract.
      const babies = [];
      for (let i = 0; i < live; i++) babies.push({ outcome: 'thriving_with_mom', sex: 'unknown', create_profile: false });
      for (let i = 0; i < born - live; i++) babies.push({ outcome: 'stillborn', sex: 'unknown', create_profile: false });

      const payload = {
        dam_id: form.dam_id,
        sire_id: form.sire_id && form.sire_id !== 'unknown' ? form.sire_id : null,
        date: form.date,
        notes: form.notes || null,
        babies,
        allow_historical: allowHistorical,
      };
      const res = await axios.post(`${API_URL}/api/birth-events`, payload, { headers: getAuthHeader() });
      // Optimistic prepend so the new birth shows in Recent Births
      // immediately and doesn't have to wait for the re-fetch below.
      const newEvent = res.data?.birth_event;
      if (newEvent) setRecent((prev) => [newEvent, ...(prev || [])].slice(0, 20));
      toast.success('Birth logged');
      setLastBirth({
        eventId: res.data?.id,
        dam: dams.find((d) => d.id === form.dam_id),
        liveCount: live,
        date: form.date,
      });
      setForm({ ...form, notes: '', number_born: 1, number_live: 1 });
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to log birth'));
    } finally {
      setSaving(false);
    }
  };

  const [pendingHistorical, setPendingHistorical] = useState(false);

  // ---- Edit-birth dialog ----
  // Full edit: date, sire, notes, and every baby's outcome/sex/weight.
  // dam is intentionally NOT editable (lineage integrity).
  const [editingBirth, setEditingBirth] = useState(null);
  const [editBirthSaving, setEditBirthSaving] = useState(false);
  const [editBirthHistorical, setEditBirthHistorical] = useState(false);

  const openEditBirth = (event) => {
    setEditBirthHistorical(false);
    setEditingBirth({
      id: event.id,
      date: (event.date || '').slice(0, 10),
      sire_id: event.sire_id || 'unknown',
      dam_name: event.dam_name || '',
      notes: event.notes || '',
      babies: (event.babies || []).map((b) => ({
        outcome: b.outcome || 'thriving_with_mom',
        sex: b.sex || 'unknown',
        birth_weight: b.birth_weight ?? '',
        weight_unit: b.weight_unit || 'lbs',
        notes: b.notes || '',
      })),
    });
  };

  const updateEditBaby = (idx, patch) => {
    setEditingBirth((prev) => {
      if (!prev) return prev;
      const babies = prev.babies.slice();
      babies[idx] = { ...babies[idx], ...patch };
      return { ...prev, babies };
    });
  };

  const addEditBaby = () => {
    setEditingBirth((prev) => prev ? {
      ...prev,
      babies: [...prev.babies, { outcome: 'thriving_with_mom', sex: 'unknown', birth_weight: '', weight_unit: 'lbs', notes: '' }],
    } : prev);
  };

  const removeEditBaby = (idx) => {
    setEditingBirth((prev) => {
      if (!prev || prev.babies.length <= 1) return prev;
      return { ...prev, babies: prev.babies.filter((_, i) => i !== idx) };
    });
  };

  const saveEditBirth = async (allowHistorical = false) => {
    if (!editingBirth) return;
    const dv = validateDate(editingBirth.date, { field: 'Birth date' });
    if (!dv.ok) {
      if (dv.reason === 'too_old') {
        if (!allowHistorical) { setEditBirthHistorical(true); return; }
      } else {
        toast.error(dv.message); return;
      }
    }
    for (let i = 0; i < editingBirth.babies.length; i++) {
      const b = editingBirth.babies[i];
      if (b.birth_weight !== '' && b.birth_weight !== null) {
        const w = Number(b.birth_weight);
        if (!Number.isFinite(w) || w <= 0) {
          toast.error(`Baby ${i + 1} weight must be greater than 0`);
          return;
        }
      }
    }
    setEditBirthSaving(true);
    try {
      const payload = {
        date: editingBirth.date,
        sire_id: editingBirth.sire_id === 'unknown' ? '' : editingBirth.sire_id,
        notes: editingBirth.notes,
        babies: editingBirth.babies.map((b) => ({
          outcome: b.outcome,
          sex: b.sex,
          birth_weight: b.birth_weight === '' || b.birth_weight == null ? null : Number(b.birth_weight),
          weight_unit: b.weight_unit,
          notes: b.notes || null,
          create_profile: false,
        })),
        allow_historical: allowHistorical,
      };
      const res = await axios.put(`${API_URL}/api/birth-events/${editingBirth.id}`, payload, { headers: getAuthHeader() });
      const updated = res.data?.birth_event;
      if (updated) setRecent((prev) => prev.map((ev) => (ev.id === updated.id ? { ...ev, ...updated } : ev)));
      toast.success('Birth updated');
      setEditingBirth(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to update birth'));
    } finally {
      setEditBirthSaving(false);
    }
  };

  return (
    <>
      <Card className="card-farm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" /> Quick Birth Entry</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Dam *</label>
            <Select value={form.dam_id} onValueChange={(v) => v === '__add__' ? setShowAddDam(true) : setForm({ ...form, dam_id: v })}>
              <SelectTrigger data-testid="birth-dam-select"><SelectValue placeholder="Select dam (mother)" /></SelectTrigger>
              <SelectContent>
                {dams.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name} · {d.animal_type}</SelectItem>))}
                <SelectItem value="__add__">+ Add new doe / ewe / cow</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Sire (optional)</label>
            <Select value={form.sire_id || 'unknown'} onValueChange={(v) => v === '__add__' ? setShowAddSire(true) : setForm({ ...form, sire_id: v })}>
              <SelectTrigger data-testid="birth-sire-select"><SelectValue placeholder="Sire" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unknown">Unknown / pastured</SelectItem>
                {sires.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.animal_type}</SelectItem>))}
                <SelectItem value="__add__">+ Add new sire</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Born</label>
              <Input type="number" min="1" value={form.number_born}
                onChange={(e) => {
                  const born = parseInt(e.target.value) || 0;
                  setForm({ ...form, number_born: born, number_live: Math.min(born, form.number_live || born) });
                }}
                data-testid="birth-born-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Live</label>
              <Input type="number" min="0" value={form.number_live}
                onChange={(e) => setForm({ ...form, number_live: parseInt(e.target.value) || 0 })}
                data-testid="birth-live-input" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              placeholder="e.g., difficult birth, dam needed assistance" />
          </div>

          <Button onClick={() => submit()} disabled={saving} className="w-full" data-testid="birth-log-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Baby className="w-4 h-4 mr-2" />} Log Birth
          </Button>
        </CardContent>
      </Card>

      {/* Season summary rollup — the backend returns total_babies /
          livability_rate (already a percent 0-100) / avg_babies_per_birth. */}
      <Card className="card-farm bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-700" /> Season Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3 text-sm" data-testid="birth-season-summary">
            <SummaryTile label="Total born" value={season?.total_babies ?? 0} />
            <SummaryTile label="Survival" value={season?.livability_rate != null ? `${Math.round(season.livability_rate)}%` : '—'} />
            <SummaryTile label="Avg litter" value={season?.avg_babies_per_birth != null ? Number(season.avg_babies_per_birth).toFixed(1) : '—'} />
          </div>
        </CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent Births</CardTitle></CardHeader>
        <CardContent data-testid="birth-recent-list">
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">No births logged yet.</p>
          ) : (
            <ul className="divide-y">
              {recent.slice(0, 12).map((e, i) => (
                <li key={e.id || i} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.dam_name || 'Dam'}</div>
                    <div className="text-xs text-muted-foreground">{formatDateShort(e.date)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">{e.babies?.length || 0} born</div>
                    <div className="text-xs text-emerald-700">{(e.babies || []).filter((b) => b.outcome !== 'stillborn').length} live</div>
                  </div>
                  {e.id && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                      onClick={() => openEditBirth(e)}
                      data-testid={`edit-birth-${e.id}`}
                      aria-label="Edit birth"><Edit2 className="w-3.5 h-3.5" /></Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Post-birth "track individually" prompt */}
      <Dialog open={!!lastBirth} onOpenChange={(v) => !v && setLastBirth(null)}>
        <DialogContent data-testid="birth-track-individually-dialog">
          <DialogHeader>
            <DialogTitle>Track any of these babies individually?</DialogTitle>
            <DialogDescription>
              {lastBirth?.liveCount || 0} live baby(ies) logged for {lastBirth?.dam?.name}. If you plan to name and track them as
              individual animals (foundation stock), open the Manage tab and add each as a new foundation animal —
              linking dam and sire will already be in place.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setLastBirth(null)} data-testid="birth-track-skip">Not now</Button>
            <Button className="flex-1"
              onClick={() => { setLastBirth(null); window.location.hash = 'add-foundation'; }}
              data-testid="birth-track-open">
              Open Manage tab
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline dam/sire creation is delegated to Manage tab to avoid duplicating full animal-form logic. */}
      <InlineAnimalCreator open={showAddDam} onOpenChange={setShowAddDam} sex="female" onCreated={(a) => { setDams((d) => [...d, a]); setForm((f) => ({ ...f, dam_id: a.id })); }} />
      <InlineAnimalCreator open={showAddSire} onOpenChange={setShowAddSire} sex="male" onCreated={(a) => { setSires((s) => [...s, a]); setForm((f) => ({ ...f, sire_id: a.id })); }} />

      {/* Edit Birth Dialog — full edit including per-baby outcome/sex/weight.
          dam_id is intentionally not surfaced (immutable to keep lineage sane). */}
      <Dialog open={!!editingBirth} onOpenChange={(v) => !v && setEditingBirth(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-birth-dialog">
          <DialogHeader>
            <DialogTitle>Edit birth event</DialogTitle>
            <DialogDescription>
              Dam: <span className="font-medium">{editingBirth?.dam_name || 'Dam'}</span> (dam can't be changed)
            </DialogDescription>
          </DialogHeader>
          {editingBirth && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Date</label>
                  <Input type="date" value={editingBirth.date}
                    onChange={(e) => setEditingBirth({ ...editingBirth, date: e.target.value })}
                    data-testid="edit-birth-date-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Sire</label>
                  <Select value={editingBirth.sire_id}
                    onValueChange={(v) => setEditingBirth({ ...editingBirth, sire_id: v })}>
                    <SelectTrigger data-testid="edit-birth-sire-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Unknown / pastured</SelectItem>
                      {sires.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.animal_type}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Notes</label>
                <Textarea rows={2} value={editingBirth.notes}
                  onChange={(e) => setEditingBirth({ ...editingBirth, notes: e.target.value })}
                  data-testid="edit-birth-notes-input" />
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Babies ({editingBirth.babies.length})</div>
                  <Button size="sm" variant="outline" onClick={addEditBaby}
                    data-testid="edit-birth-add-baby-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add baby
                  </Button>
                </div>
                {editingBirth.babies.map((b, idx) => (
                  <div key={idx} className="border rounded-md p-2 space-y-2 bg-muted/30"
                    data-testid={`edit-birth-baby-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Baby {idx + 1}</span>
                      {editingBirth.babies.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-6 w-6"
                          onClick={() => removeEditBaby(idx)}
                          data-testid={`edit-birth-remove-baby-${idx}`}
                          aria-label="Remove baby">
                          <X className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] uppercase text-muted-foreground">Outcome</label>
                        <Select value={b.outcome} onValueChange={(v) => updateEditBaby(idx, { outcome: v })}>
                          <SelectTrigger data-testid={`edit-birth-outcome-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="thriving_with_mom">Thriving with mom</SelectItem>
                            <SelectItem value="bottle_fed">Bottle-fed</SelectItem>
                            <SelectItem value="weak">Weak</SelectItem>
                            <SelectItem value="died_within_24h">Died within 24h</SelectItem>
                            <SelectItem value="died_later">Died later</SelectItem>
                            <SelectItem value="stillborn">Stillborn</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-muted-foreground">Sex</label>
                        <Select value={b.sex} onValueChange={(v) => updateEditBaby(idx, { sex: v })}>
                          <SelectTrigger data-testid={`edit-birth-sex-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unknown">Unknown</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <label className="text-[10px] uppercase text-muted-foreground">Birth weight</label>
                        <Input type="number" step="0.1" min="0" value={b.birth_weight}
                          onChange={(e) => updateEditBaby(idx, { birth_weight: e.target.value })}
                          placeholder="Optional"
                          data-testid={`edit-birth-weight-${idx}`} />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-muted-foreground">Unit</label>
                        <Select value={b.weight_unit} onValueChange={(v) => updateEditBaby(idx, { weight_unit: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lbs">lbs</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="oz">oz</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditingBirth(null)}>Cancel</Button>
                <Button className="flex-1" onClick={() => saveEditBirth()} disabled={editBirthSaving}
                  data-testid="edit-birth-save-btn">
                  {editBirthSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Historical-edit confirmation (>5 yr) */}
      <Dialog open={editBirthHistorical} onOpenChange={(v) => !v && setEditBirthHistorical(false)}>
        <DialogContent data-testid="edit-birth-historical-confirm">
          <DialogHeader>
            <DialogTitle>Save as historical entry?</DialogTitle>
            <DialogDescription>
              The date you entered ({editingBirth?.date}) is more than 5 years ago. Confirm to save.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setEditBirthHistorical(false)}>Cancel</Button>
            <Button className="flex-1"
              onClick={() => { setEditBirthHistorical(false); saveEditBirth(true); }}
              data-testid="edit-birth-historical-confirm-btn">
              Save as historical
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historical (>5yr) birth-date confirmation */}
      <Dialog open={pendingHistorical} onOpenChange={(v) => !v && setPendingHistorical(false)}>
        <DialogContent data-testid="birth-historical-confirm">
          <DialogHeader>
            <DialogTitle>Log historical entry?</DialogTitle>
            <DialogDescription>
              The date you entered ({form.date}) is more than 5 years ago. If this is intentional
              (backfilling old births), we can save it — otherwise please pick a more recent date.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setPendingHistorical(false)}>Cancel</Button>
            <Button className="flex-1" onClick={() => { setPendingHistorical(false); submit(true); }} data-testid="birth-historical-confirm-btn">
              Save as historical
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InlineAnimalCreator({ open, onOpenChange, sex, onCreated }) {
  const { getAuthHeader } = useAuth();
  const [name, setName] = useState('');
  const [animalType, setAnimalType] = useState('goat');
  const [breed, setBreed] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Enter a name'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/animals/foundation`, {
        name: name.trim(), animal_type: animalType, sex, breed: breed || null, is_foundation: true,
      }, { headers: getAuthHeader() });
      // Backend returns { message, animal: {...} }; older shape may return the animal directly.
      const created = res.data?.animal || res.data;
      toast.success(`${sex === 'female' ? 'Dam' : 'Sire'} added`);
      onCreated?.(created);
      setName(''); setBreed('');
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to add'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`inline-add-${sex === 'female' ? 'dam' : 'sire'}-dialog`}>
        <DialogHeader>
          <DialogTitle>Add new {sex === 'female' ? 'doe / ewe / cow (dam)' : 'buck / ram / bull (sire)'}</DialogTitle>
          <DialogDescription>Quick add so you can log this birth. You can add photos, weight, and full detail later in the Manage tab.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={sex === 'female' ? 'e.g., Bessie' : 'e.g., Big Boy'} data-testid="inline-animal-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Species</label>
              <Select value={animalType} onValueChange={setAnimalType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="goat">Goat</SelectItem>
                  <SelectItem value="sheep">Sheep</SelectItem>
                  <SelectItem value="cattle">Cattle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Breed (optional)</label>
              <Input value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="e.g., Boer" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="flex-1" onClick={submit} disabled={saving} data-testid="inline-animal-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-lg bg-white/70 border border-emerald-200 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-emerald-700/70">{label}</div>
      <div className="font-heading text-xl font-bold text-emerald-800 tabular-nums">{value}</div>
    </div>
  );
}

// ============================================================================
// HEALTH TAB
// ============================================================================
function HealthTab() {
  const { getAuthHeader } = useAuth();
  const [foundationAnimals, setFoundationAnimals] = useState([]);
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    animal_id: '', record_type: 'checkup', description: '', vet_name: '', cost: '', date: todayLocal(), notes: '',
  });

  const load = async () => {
    try {
      const fRes = await axios.get(`${API_URL}/api/animals/foundation`, { headers: getAuthHeader() });
      const animals = (fRes.data?.animals || fRes.data || []).filter((a) => ['goat', 'sheep', 'cattle'].includes(a.animal_type));
      setFoundationAnimals(animals);
      // Aggregate every scope in one call — includes herd-level records
      // (animal_type null) that per-species queries would miss.
      const all = [];
      try {
        const r = await axios.get(`${API_URL}/api/livestock/health/all`, { headers: getAuthHeader() });
        all.push(...(r.data?.records || []));
      } catch { /* skip */ }
      all.sort((a, b) => ((b.date || '') < (a.date || '') ? -1 : 1));
      setRecords(all.slice(0, 20));
    } catch { /* soft-fail */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async (allowHistorical = false) => {
    if (!form.description.trim()) { toast.error('Add a description'); return; }
    if (form.cost && Number(form.cost) < 0) { toast.error('Cost cannot be negative'); return; }
    const dv = validateDate(form.date, { field: 'Date' });
    if (!dv.ok) {
      if (dv.reason === 'too_old') {
        if (!allowHistorical) {
          setPendingHistorical(true);
          return;
        }
      } else {
        toast.error(dv.message);
        return;
      }
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/livestock/health`, {
        foundation_animal_id: form.animal_id || null,
        record_type: form.record_type,
        description: form.description,
        vet_name: form.vet_name || null,
        cost: form.cost ? parseFloat(form.cost) : null,
        date: form.date,
        notes: form.notes || null,
        allow_historical: allowHistorical,
      }, { headers: getAuthHeader() });
      toast.success('Health record logged');
      setForm({ ...form, description: '', vet_name: '', cost: '', notes: '' });
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to log health record'));
    } finally {
      setSaving(false);
    }
  };

  const [pendingHistorical, setPendingHistorical] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDelete = async (record) => {
    setConfirmDeleteId(null);
    setRecords((rs) => rs.filter((r) => r.id !== record.id));
    try {
      await axios.delete(`${API_URL}/api/livestock/health/record/${record.id}`, { headers: getAuthHeader() });
      let undone = false;
      toast.success('Health record deleted', {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            undone = true;
            try {
              await axios.post(`${API_URL}/api/livestock/health`, {
                foundation_animal_id: record.foundation_animal_id || null,
                animal_type: record.animal_type || null,
                record_type: record.record_type || 'checkup',
                description: record.description || '(restored)',
                vet_name: record.vet_name || null,
                cost: record.cost ?? null,
                date: record.date,
                notes: record.notes || null,
                allow_historical: true,
              }, { headers: getAuthHeader() });
              toast.success('Restored');
              await load();
            } catch {
              toast.error('Restore failed');
              await load();
            }
          },
        },
      });
      setTimeout(() => { if (!undone) load(); }, 5100);
    } catch {
      toast.error('Delete failed');
      await load();
    }
  };

  return (
    <>
      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" /> Quick Health Entry</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Type</label>
              <Select value={form.record_type} onValueChange={(v) => setForm({ ...form, record_type: v })}>
                <SelectTrigger data-testid="health-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checkup">Checkup</SelectItem>
                  <SelectItem value="vaccination">Vaccination</SelectItem>
                  <SelectItem value="treatment">Treatment</SelectItem>
                  <SelectItem value="incident">Incident</SelectItem>
                  <SelectItem value="deworming">Deworming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Animal (optional)</label>
            <Select value={form.animal_id || 'none'} onValueChange={(v) => setForm({ ...form, animal_id: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Herd-level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Herd-level (unattributed)</SelectItem>
                {foundationAnimals.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name} · {a.animal_type}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Description *</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g., Annual CDT vaccination" data-testid="health-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Vet name</label>
              <Input value={form.vet_name} onChange={(e) => setForm({ ...form, vet_name: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Cost ($)</label>
              <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional" />
          </div>
          <Button onClick={() => submit()} disabled={saving} className="w-full" data-testid="health-log-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />} Log Health Record
          </Button>
        </CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent Records</CardTitle></CardHeader>
        <CardContent data-testid="health-recent-list">
          {records.length === 0 ? (
            <p className="text-sm text-muted-foreground">No health records yet.</p>
          ) : (
            <ul className="divide-y">
              {records.map((r, i) => (
                <li key={r.id || i} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium capitalize">{r.record_type || 'record'}</div>
                    <div className="text-xs text-muted-foreground truncate">{r.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-muted-foreground">{formatDateShort(r.date)}</div>
                    {r.cost != null && <div className="font-mono text-xs">${Number(r.cost).toFixed(2)}</div>}
                  </div>
                  {confirmDeleteId === r.id ? (
                    <span className="flex items-center gap-1 ml-1">
                      <span className="text-xs text-muted-foreground">Delete?</span>
                      <Button size="icon" variant="destructive" className="h-7 w-7"
                        onClick={() => handleDelete(r)}
                        data-testid={`health-delete-confirm-${r.id}`}
                        aria-label="Confirm delete"
                      ><Check className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7"
                        onClick={() => setConfirmDeleteId(null)}
                        data-testid={`health-delete-cancel-${r.id}`}
                        aria-label="Cancel delete"
                      ><X className="w-3.5 h-3.5" /></Button>
                    </span>
                  ) : (
                    r.id && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmDeleteId(r.id)}
                        data-testid={`health-delete-${r.id}`}
                        aria-label="Delete health record"
                      ><Trash2 className="w-3.5 h-3.5" /></Button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={pendingHistorical} onOpenChange={(v) => !v && setPendingHistorical(false)}>
        <DialogContent data-testid="health-historical-confirm">
          <DialogHeader>
            <DialogTitle>Log historical entry?</DialogTitle>
            <DialogDescription>
              The date you entered ({form.date}) is more than 5 years ago. If this is intentional
              (backfilling old records), we can save it — otherwise please pick a more recent date.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setPendingHistorical(false)}>Cancel</Button>
            <Button className="flex-1" onClick={() => { setPendingHistorical(false); submit(true); }} data-testid="health-historical-confirm-btn">
              Save as historical
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
